import { Flash } from 'AWS-UI-Components-React';
import React from 'react';
import {
  DOWNLOAD_FILE_NAME,
  OPERATION_STATUS,
  REPORT_KEYS,
  SNAPSHOT_ID,
  FORECAST_REPORT,
  MAX_PAYLOADS_THRESHOLD,
  TOGGLE_PLACEMENT,
} from './common/constants';
import { COMPONENT_TO_LINK } from '../../constants/urls';

import ComponentLink from '../content/controls/component_link';
import { formatHeadingDisplay, formatCellDisplay, getFormat } from './utils/formatting';
import { zip, isEmpty } from './utils';
import { ReportTable } from './components/report_table';
import { buildContentSelectorOption } from './utils/report_preferences';
import { REPORT_TYPE } from '../../constants/report_type';
import { getCompositeKeys, getForEachCompositeKey } from './utils/composite_keys';
import {
  accuracyDataWithCompositeKeys,
  replaceLiveSnapshotId,
  transformToReportFormat
} from './utils/transformations';
import { reportDataByColumn } from './utils/download';
import { sortByComponentProductLineChannel, sortByMarketplaceId } from './utils/sorting';

/**
 * Renders a Forecast Attribution report. Contains methods that are specific
 * to the Forecast Attribution report - such as transforming the data to Forecast Attribution
 * report format.
 */
export class ForecastAttributionReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
    };
    this.reportData = null;
    this.contentSelectorOptions = null;
  }

  componentDidUpdate = (prevProps) => {
    if (
      prevProps.downloadStatus !== this.props.downloadStatus &&
      this.props.downloadStatus === OPERATION_STATUS.IN_PROGRESS
    ) {
      this.downloadData();
    }
  };

  downloadData = () => {
    if(!this.isThresholdCrossed()) {
      this.reportData.reportHeadings = this.reportData.reportHeadings.filter(e => e !== 'Marketplace');
      this.reportData.reportKeys = this.reportData.reportKeys.filter(e => e !== 'marketplace');
    }
    const reportData = {
      ...this.reportData,
      reportRows: this.reportData.reportRows.map((r) => {
        return { ...r, visible: true };
      }),
    };

    let dataToDownload = reportDataByColumn(reportData);

    this.props.downloadFile(dataToDownload.join('\n'), DOWNLOAD_FILE_NAME);
    this.props.onDownloadDataSuccess();
  };

  parsePayloadData = () => {
    const { requestPayloads, responsePayloads, liveSnapshot } = this.props;

    const dataSetKeys = [SNAPSHOT_ID];

    let compositeKeys = getCompositeKeys(replaceLiveSnapshotId(requestPayloads, liveSnapshot), dataSetKeys);
    let accuracyData = accuracyDataWithCompositeKeys(responsePayloads, compositeKeys, FORECAST_REPORT.DEDUPED_COL_KEYS);

    const sortedHeadings = [...new Set(responsePayloads.map((p) => p.sortedHeadings).flat())];
    const sortedKeys = [...new Set(responsePayloads.map((p) => p.sortedKeys).flat())];

    const sortedHeadingsWithCompositeKeys = getForEachCompositeKey(sortedHeadings, compositeKeys);
    const sortedKeysWithCompositeKeys = getForEachCompositeKey(sortedKeys, compositeKeys);

    const transformedData = transformToReportFormat(
      sortedKeysWithCompositeKeys,
      sortedHeadingsWithCompositeKeys,
      accuracyData,
      FORECAST_REPORT.DEDUPED_COL_KEYS
    );

    return this.isThresholdCrossed ? { ...transformedData, reportRows: sortByMarketplaceId(transformedData.reportRows) } :
      { ...transformedData, reportRows: sortByComponentProductLineChannel(transformedData.reportRows) };
  };

  getColumnDefinitions = (reportData) => {
    const { reportKeys, reportHeadings } = reportData;
    const { columnFormats, displayInMillions } = this.props;

    let columns = [];

    for (let [key, heading] of zip(reportKeys, reportHeadings)) {

      // we don't want to display the marketplace Id column when all marketplaces are not selected
      if(key.endsWith(REPORT_KEYS.MARKETPLACE) && !this.isThresholdCrossed()) {
        continue;
      }

      let columnEntry = {};
      columnEntry['id'] = key;
      columnEntry['header'] = formatHeadingDisplay(heading, getFormat(key, columnFormats));
      columnEntry['minWidth'] = FORECAST_REPORT.WRAP_LINE_COLUMNS.includes(key) ? '200px' : '100px';
      columnEntry['allowLineWrap'] =
        FORECAST_REPORT.WRAP_LINE_COLUMNS.includes(key) || FORECAST_REPORT.WRAP_LINE_COLUMNS.some((c) => key.endsWith(c));
      columnEntry['cell'] = (item) => {
        return (key === REPORT_KEYS.COMPONENT || key.endsWith(REPORT_KEYS.BASE_FACTOR)) ? (
          <ComponentLink componentName={item[key]} mapping={COMPONENT_TO_LINK} level={item.level} />
        ) : (
          formatCellDisplay(item[key], getFormat(key, columnFormats), displayInMillions, item)
        );
      };

      // Set sortable columns
      const sortableColumns = this.getSortableColumns(reportData);
      if (sortableColumns.map(c => c.id).includes(key)) {
        columnEntry['sortingField'] = key;
      }

      columns.push(columnEntry);
    }

    return columns;
  };

  onToggleMillions = () => {
    this.props.onToggleMillions();
  };

  onContentSelectionChange = (event) => {
    this.props.onContentSelectionChange(event);
  };

  getContentSelectorOptions = (reportKeys, reportHeadings) => {
    const { reportPreferences, columnSets, columnsJson } = this.props;

    const combinedColumnSets = {
      ...columnSets,
      columnsForForecastAllMarketplaces: this.isThresholdCrossed()
                                 ? columnsJson.columnsForForecastAllMarketplaces
                                 : null,
    };

    if(!this.isThresholdCrossed()) {
      reportHeadings = reportHeadings.filter(e => e !== 'Marketplace');
      reportKeys = reportKeys.filter(e => e !== 'marketplace');
    }
    return [
      {
        options: reportKeys.map((key, i) =>
          buildContentSelectorOption(key, reportHeadings[i], combinedColumnSets, reportPreferences, REPORT_TYPE.FORECAST)
        ),
      },
    ];
  };

  getSortableColumns = (reportData) => {
    const { reportKeys } = reportData;
    let sortableColumns = [];
    for (let reportKey of Object.values(reportKeys)) {
      if(FORECAST_REPORT.SORTABLE_COLUMNS.some((c) => reportKey.endsWith(c))) {
        let config = {};
        config.id = reportKey;
        config.field = reportKey;
        sortableColumns.push(config);
      }
    }

    return sortableColumns;
  };

  isThresholdCrossed = () => {
    const { requestPayloads } = this.props;
    return requestPayloads.length > MAX_PAYLOADS_THRESHOLD;
  };

  render() {
    const { loading, displayInMillions, loadError } = this.props;

    if (isEmpty(this.reportData)) {
      this.reportData = this.parsePayloadData();

      const { reportKeys, reportHeadings } = this.reportData;
      this.contentSelectorOptions = this.getContentSelectorOptions(reportKeys, reportHeadings);
      this.props.onContentSelectorOptionsRegenerate(this.contentSelectorOptions);
    }
    const { reportRows } = this.reportData;

    return (
      <div>
        {loadError &&
          <div>
            <Flash dismissible={true} type="error">
              {`Error fetching data. One of the snapshots might not have the PGMS columns. Try reselecting snapshot.`}
            </Flash>
          </div>
        }
        <ReportTable
          loading={loading}
          displayInMillions={displayInMillions}
          reportRows={reportRows}
          columnDefinitions={this.getColumnDefinitions(this.reportData)}
          contentSelectorOptions={this.contentSelectorOptions}
          onContentSelectionChange={this.onContentSelectionChange}
          togglePlacement={TOGGLE_PLACEMENT.IN_HEADER}
          onToggleMillions={this.onToggleMillions}
          showTableFilter={true}
          wrapLines={true}
          visibleColumns={this.props.visibleColumns}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/forecast_attribution_report.js